import React, { useContext, useEffect, useState } from 'react';
import { compose, pick, prop } from 'ramda';
import { withProps } from 'recompose';
import { withPortlet } from 'js/components/system/Portlet/hocs/withPortlet';
import { useAppSelector, useDeviceSelector } from 'js/store/hooks';
import { useMountEffect } from 'js/hooks/useMountEffect';
import {
    fetchActiveList,
    fetchDetailsByIds,
    fetchHistoricalList,
    setListAction,
    subscribeSummary,
    unsubscribeSummary,
    updateListAction
} from './actions';
import { ListContext } from './context';
import { withListContext } from './withContext';
import { usePushSubscription } from 'server';

export const useFetchingLeaderBoards = (props) => {
    const { isHistory, leaderboardIds = [] } = props;
    const { state = {}, dispatch } = useContext(ListContext);
    const [error, setError] = useState();
    const { leaderboards } = state;

    useMountEffect((isMounted) => {
        const fetchPromise = leaderboardIds.length
            ? fetchDetailsByIds(props)
            : isHistory
                ? fetchHistoricalList(props)
                : fetchActiveList(props);

        fetchPromise
            .then(({ data }) => isMounted() && dispatch(setListAction(data.leaderboards)))
            .catch(({ data }) => isMounted() && setError(data));
    }, []);

    return { leaderboards, error };
};

export const useSubscriptionOnSummaryUpdates = (props) => {
    const {
        clientType,
        clientPlatform,
        game,
        notificationFieldsWhitelist,
        responseFieldsWhitelist
    } = props;

    useEffect(() => {
        subscribeSummary({
            clientType,
            clientPlatform,
            game,
            notificationFieldsWhitelist,
            responseFieldsWhitelist
        });

        return () => unsubscribeSummary();
    }, [clientPlatform, clientType, game, notificationFieldsWhitelist, responseFieldsWhitelist]);
};

export const useUpdateLeaderBoards = () => {
    const { dispatch } = useContext(ListContext);

    usePushSubscription([71031, 71032], () => message => dispatch(updateListAction(message.data)));
};

export const withPlatform = withProps(() => useDeviceSelector(pick(['clientPlatform'])));

export const withFetchListActions = compose(
    withPlatform,
    withProps(() => {
        const clientType = useAppSelector(prop('clientType'));

        return {
            clientType
        };
    })
);

export const withFetch = compose(
    withFetchListActions,
    withProps(useFetchingLeaderBoards)
);

export const withSubscribeSummaryActions = compose(
    withPlatform,
    withProps(() => {
        const clientType = useAppSelector(prop('clientType'));

        return {
            clientType
        };
    })
);

export const withUpdates = compose(
    withSubscribeSummaryActions,
    Wrapped => (props) => {
        useSubscriptionOnSummaryUpdates(props);
        useUpdateLeaderBoards();

        return <Wrapped {...props} />;
    }
);

export const withLeaderBoards = compose(
    withPortlet({ forLoggedIn: true }),
    withListContext,
    withFetch
);

export const withLeaderBoardsAndUpdates = compose(
    withLeaderBoards,
    withUpdates
);

export default withLeaderBoardsAndUpdates;
